// @givmo/app-admin — the company DIRECT-GIVING confirm→charge→success dialog (EP-WEB1). ★ MONEY-MOVING ★
//
// A company gives its OWN money (funding_source="company") to a charity / ETF / fundraiser. Confirming
// CHARGES a company card or DEBITS the company wallet via `POST /charities|etfs|fundraisers/{id}/
// donations` — the same live donation routes the consumer apps use, here with the company-funding
// body. This is where every money-safety invariant is enforced (mirrors <ApproveMatchDialog> and the
// EP-WEB6 <DepositDialog>):
//
//   1. IDEMPOTENCY — a stable `Idempotency-Key` is minted ONCE per attempt-series and REUSED for any
//      retry of the SAME gift. The series is PAGE-OWNED (passed in as `series`: the key AND the request
//      it was minted for) and keyed by the full gift signature (target + amount + funding + the
//      cover-fees tick), so it SURVIVES a close/reopen — even a full dialog unmount: re-entering the
//      identical gift after a maybe-charged close restores its tick, re-shows the request it made
//      (its figures and its disclosure) and resends THAT body under THAT key (CAM-R59b, cut 7 review
//      F2), so the backend replays the first outcome — or refuses a stale echo or price before
//      charging — rather than refusing a body that moved under the held key. Any edit mints fresh by
//      construction. The backend `claim_idempotency` de-dupes only same-key retries; a fresh key on
//      the same gift is exactly the double-charge vector, so the series is NEVER cleared on an
//      open-transition.
//   2. NO BLIND RETRY of a MAYBE-CHARGED error — a 408/429/5xx/transport failure is RETRYABLE (the
//      charge may have gone through): surface an explicit check-status/try-again state and re-POST
//      under the SAME key only on the user's say-so. A card decline surfaces here (the backend maps a
//      processor rejection to 502 → retryable, per the shipped charge classifier — we do not fight it).
//      DISMISSAL of the maybe-charged state is itself gated: Esc / overlay-click / Cancel do not close
//      silently — the user must explicitly acknowledge that the charge may have gone through.
//   3. SINGLE-FLIGHT — Confirm disables + spins while a charge is in flight; a double-click cannot fire
//      a second POST (an in-flight ref AND the disabled button) nor mint a second key.
//   4. CONFIRM-BEFORE-CHARGE — the dialog restates amount + target + funding source before any charge.
//      The gift itself is FIXED for the dialog's open-session (it arrives as props and the page cannot
//      be edited behind the modal), so no divergent series can start while a charge may have gone
//      through — the field-lock invariant holds structurally.
//   5. CARD/WALLET REQUIRED — the page only opens this with a resolved funding source (a card id or the
//      wallet); there is no chargeless path here.
//   6. INSUFFICIENT-FUNDS is a FIRST-CLASS fatal state — a wallet gift over balance returns 409
//      "Insufficient funds"; the page also PRE-SURFACES it (disables Review) when the balance is known,
//      and this dialog states it before any charge when the gross it states, which covering the fees
//      raises above the gift, is over the balance the page hands it (CAM-R80).
//   7. DISCLOSURE-BEFORE-CHARGE (CAM-X4 web) — the company is this gift's donor, so the dialog resolves
//      the COMPANY-payer body for the gift's target and rail (`give-disclosure.ts`), shows it under the
//      "Terms of your donation" divider directly above the confirm control (D-CAM-41: the dialog leads
//      with the donation summary; the full text sits in a bounded region below it, never collapsed),
//      and echoes it (`shown_disclaimer_*`) on the donation body. The
//      Give control stays disarmed until the body resolves; a failed resolve says so and offers a retry
//      — the gift never goes out without its text. The gift is fixed for the open-session, so the
//      body resolves once per open and never changes under an armed Give except through the one
//      sanctioned re-resolve: a `409 DISCLOSURE_STALE` (a definite no-charge — the gate runs before
//      the charge) drops the key, re-resolves, and asks for a fresh confirm against the new text.
//   8. THE SERVER'S PRICE (CAM-BE13, D-CAM-40) — the gift may cover its fees, as a company deposit
//      may (the "Cover processing fees" row, off on every open). Every figure the row and the charge
//      line state comes from the server's quote for this amount and funding source
//      (`components/charge-quote.ts`: the charge's own resolution — the stored card's brand row, the
//      company's own setting, its negotiated platform rate), and the gift carries the choice plus
//      the gross and fee it stated (`expected_amount_charged` / `expected_fee`); a backend that would
//      charge other figures refuses with `409 CHARGE_QUOTE_CHANGED` before money moves, and the
//      dialog re-reads the price and asks again under a new key. Give is held while the quote loads
//      or has failed. The choice is part of the charge series (a different gross is a different
//      charge), and a maybe-charged attempt's "Try again" resends EXACTLY the request it made,
//      frozen when its charge started — never a body rebuilt from a price that moved since
//      (invariant 1).
//
// CLOSE CALLBACKS (the DepositDialog two-callback pattern): `onSuccess` fires ONCE when the charge
// confirms (the page invalidates its reads); `onCloseAfterSuccess` fires when the dialog closes FROM
// the success state (the page toasts + resets the form); `onCloseAfterAttempt` fires when the dialog
// closes after a NON-success charge attempt (fatal error, an acknowledged maybe-charged close) so the
// page refreshes the wallet/dashboard reads — the "check your donations" hint must show fresh truth.
// A plain no-attempt Cancel fires neither, and must never produce a success signal.
//
// RECEIPT COPY forks by funding source on success (plan §3): CARD ⇒ a receipt is emailed (say so,
// neutrally); WALLET ⇒ receipt-less (deposit already receipted) — no receipt mention.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  sdk,
  centsToMoneyString,
  firstErrorCode,
  isGivmoError,
  supportReference,
  GivmoError,
} from '@givmo/api-client';
import { Button, Dialog, Icon, testIdProp } from '@givmo/design-system';
import { isDisclosureStaleError } from '@givmo/disclosure';
import { AlertTriangle, CheckCircle2, Wallet as WalletIcon } from 'lucide-react';
import { isRetryableChargeError, maybeChargedLine } from '@givmo/payments';
import {
  ChargeDisclosureTerms,
  DisclosureUnavailable,
  useChargeDisclosure,
} from '../../components/charge-disclosure';
import {
  CHARGE_QUOTE_CHANGED,
  breakdownFor,
  fetchGiftQuote,
  giftQuoteQueryKey,
  statedCharge,
  toChargeQuote,
} from '../../components/charge-quote';
import { ChargeSummary } from '../../components/charge-summary';
import {
  chargeQuoteFigures,
  coverFeesRowCopy,
  hasNothingToCover,
} from '../../components/cover-fees-copy';
import { CoverFeesRow } from '../../components/cover-fees-row';
import { useRevealOnShow } from '../../components/reveal-on-show';
import { parseCentsAmount } from '../dashboard/dashboard-decode';
import { formatChargeFigure, formatFeeFigure } from '../settings/wallet-decode';
import {
  buildCompanyDonationBody,
  formatWholeDollarUsd,
  giveSummaryContext,
  isInsufficientFundsError,
  receiptCopy,
  type Funding,
  type GiveTarget,
} from './give-decode';
import { giveDisclosureRail, resolveGiveDisclosure } from './give-disclosure';
import type { GiveRequest, GiveSeries, GiveSignature } from './give-idempotency';

const PREFIX = 'give';

export interface DirectGiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null while closed; the resolved gift once opened (target already selected, amount validated). */
  target: GiveTarget | null;
  /** Whole-dollar integer amount (validated > 0 by the caller). */
  amount: number;
  /** The chosen funding source: a specific company card, or the company wallet. */
  funding: Funding;
  /** Human-readable funding label restated in the confirm copy (e.g. "your Visa Card •••• 4242"). */
  fundingLabel: string;
  /**
   * The funding source as the summary's "From" row names it ("Visa Card •••• 4242", "Company
   * wallet" — `fundingSummaryLabel`). Absent → the row shows `fundingLabel`.
   */
  fundingSummary?: string;
  /** The company's name, the gift's donor of record (D-CAM-41); null while unknown. */
  companyName?: string | null;
  /**
   * The company wallet's spendable balance in exact cents, as the page read it; null or absent while
   * unknown. A wallet gift whose stated gross is over it shows the short state before any charge
   * (CAM-R80): the page pre-surfaces a gift over the balance, but only this dialog knows the gross,
   * which covering the fees raises.
   */
  walletAvailableCents?: number | null;
  /**
   * The PAGE-OWNED charge series (one per page session, held in a page `useRef`): the idempotency key
   * and the request it was minted for. It lives above this dialog so a maybe-charged series survives
   * the dialog unmounting on close: re-entering the identical gift resends the SAME request under the
   * SAME key (backend replays — no double charge); any different target/amount/funding/tick mints
   * fresh by construction (it keys by the full gift signature).
   */
  series: GiveSeries;
  /** Fired ONCE when the gift confirms — the page invalidates the wallet + dashboard reads. */
  onSuccess: () => void;
  /** Fired when the dialog closes FROM the success state — the page toasts + resets the form. */
  onCloseAfterSuccess: () => void;
  /**
   * Fired when the dialog closes after a NON-success charge attempt in this open-session (a fatal
   * error close, or an acknowledged maybe-charged close). The page refreshes the wallet + dashboard
   * queries so the balance/donations the user is told to check are fresh truth, not a stale cache.
   */
  onCloseAfterAttempt: () => void;
}

/** The charge state machine. `retryable-error` REUSES the idempotency key AND the request. */
type Phase =
  | { status: 'idle' }
  | { status: 'charging' }
  | { status: 'retryable-error'; message: string; reference: string | null }
  | { status: 'insufficient-funds'; message: string }
  | { status: 'fatal-error'; message: string; reference: string | null }
  | { status: 'disclosure-stale' }
  | { status: 'quote-changed' }
  | { status: 'success' };

const RETRYABLE_HINT =
  'We could not confirm whether the charge went through. Do not assume it failed — check your ' +
  'donations, or try again. A retry is de-duplicated and will not charge twice.';

// No "nothing has been charged" here: the resolve re-runs on a reopen, which can follow a gift that
// may have gone through (the CAM-R59 class of copy).
const DISCLOSURE_UNAVAILABLE_MESSAGE =
  'We couldn’t load the disclosure for this donation, so it can’t be sent yet.';

const DISCLOSURE_STALE_MESSAGE =
  'The disclosure for this donation was just updated. Read the updated text below, then donate ' +
  'again. Nothing was charged.';

const INSUFFICIENT_FUNDS_MESSAGE =
  'Your company wallet does not have enough available balance for this donation. Add funds to your ' +
  'wallet or pay with a company card, then try again. Nothing was charged.';

const CLOSE_ACK_BODY =
  'The charge may have gone through even though we could not confirm it. After closing, check your ' +
  'recent donations before donating again — re-entering the exact same gift (same recipient, amount, ' +
  'payment method and processing-fee choice) is safe and will not double-charge, but a different ' +
  'amount would be a new ' +
  'charge.';

const QUOTE_LOADING_CARD = 'Checking the processing fee for this card…';
const QUOTE_LOADING_WALLET = 'Checking the fees for this payment…';

const QUOTE_FAILED =
  'We could not check the processing fee for this donation, so it cannot be sent right now. ' +
  'Close and try again. Nothing will be charged.';

// Both halves: unticked, the charge is the gift itself, so most refusals are the fees alone moving
// (`expected_fee`), never the total (cut 13 review F3).
const QUOTE_CHANGED_MESSAGE =
  'The price of this donation changed — its fees or its total — so nothing was charged. Check the ' +
  'new figures, then donate again.';

export function DirectGiveDialog({
  open,
  onOpenChange,
  target,
  amount,
  funding,
  fundingLabel,
  fundingSummary,
  companyName = null,
  walletAvailableCents = null,
  series,
  onSuccess,
  onCloseAfterSuccess,
  onCloseAfterAttempt,
}: DirectGiveDialogProps) {
  const [phase, setPhase] = useState<Phase>({ status: 'idle' });
  // The maybe-charged close acknowledgement step (invariant 2): armed when the user tries to dismiss
  // a `retryable-error` state; only its explicit confirm button closes.
  const [closeAck, setCloseAck] = useState(false);
  // It replaces the footer, so on a phone it opens below the fold: it is brought into view (P5).
  const closeAckRef = useRevealOnShow<HTMLDivElement>(
    phase.status === 'retryable-error' && closeAck,
  );
  // CAM-BE13 (D-CAM-40): the per-gift "Cover processing fees" choice — off on every open.
  const [coverFees, setCoverFees] = useState(false);
  const queryClient = useQueryClient();

  // Synchronous single-flight guard: flips true the instant a charge starts, back to false when it
  // settles — a second click in the same tick is a no-op even before `phase` re-renders the button.
  const inFlight = useRef(false);
  // Whether ANY charge attempt fired in this open-session — drives `onCloseAfterAttempt` on close.
  const attempted = useRef(false);

  // Reset ONLY on a genuine CLOSED→OPEN transition: clear the phase + the ack step + the attempt flag.
  // ★ The series is DELIBERATELY NOT cleared here (and not on unmount — it is page-owned). It keys by
  // the full gift signature, so persisting it across close/reopen keeps a maybe-charged gift's series
  // alive: reopening with the SAME target+amount+funding restores its cover-fees tick and resends its
  // request under its key (the backend replays — no double charge), while any different gift mints
  // fresh by construction. Clearing on reopen was the escape hatch that made close→reopen→same-gift
  // a double charge the backend could not dedupe. The series still clears on confirmed success and on
  // a definite no-charge.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setPhase({ status: 'idle' });
      setCloseAck(false);
      setCoverFees(
        target === null
          ? false
          : (series.heldChoiceFor({
              targetType: target.type,
              targetId: target.id,
              amount,
              funding,
            }) ?? false),
      );
      attempted.current = false;
      inFlight.current = false;
    }
    prevOpen.current = open;
  }, [open]);

  const busy = phase.status === 'charging';

  // ── The donor disclosure (invariant 7): resolved per open for this gift's target + rail. ──
  const rail = giveDisclosureRail(funding);
  const disclosureKey = open && target !== null ? `${target.type}:${target.id}|${rail}` : null;
  const { state: disclosureState, reload: reloadDisclosure } = useChargeDisclosure(
    disclosureKey,
    () => (target === null ? Promise.resolve(null) : resolveGiveDisclosure(target, rail)),
  );
  const disclosure = disclosureState.status === 'ready' ? disclosureState.value : null;

  // ── The server's price (invariant 8): both requests the gift can make, for this amount + funding
  //    source. `staleTime: 0` — a price is read fresh each time it is stated (the charge's guard
  //    backs it anyway). ──
  const wireAmount = centsToMoneyString(Math.round(amount * 100)) ?? '0.00';
  const quoteQuery = useQuery({
    queryKey: giftQuoteQueryKey(wireAmount, funding),
    queryFn: () => fetchGiftQuote(wireAmount, funding),
    enabled: open && target !== null,
    select: toChargeQuote,
    staleTime: 0,
  });
  const quoteStatus: 'none' | 'loading' | 'failed' | 'ready' =
    target === null
      ? 'none'
      : quoteQuery.isPending
        ? 'loading'
        : quoteQuery.isError || quoteQuery.data == null
          ? 'failed'
          : 'ready';
  const liveQuote = quoteStatus === 'ready' ? (quoteQuery.data ?? null) : null;

  // The request held for this gift and tick (invariant 1): in flight, in doubt, or re-entered after a
  // maybe-charged close. While one is held the dialog states what it will resend — its figures and
  // its disclosure; otherwise the live reads.
  const signature: GiveSignature | null =
    target === null
      ? null
      : { targetType: target.type, targetId: target.id, amount, funding, coverFees };
  const heldRequest = signature === null ? null : series.heldFor(signature);
  const shownQuote = heldRequest?.quote ?? liveQuote;
  const shownDisclosure = heldRequest?.disclosure ?? disclosure;
  const figures = shownQuote === null ? null : chargeQuoteFigures(shownQuote);
  const companyCoversFees = shownQuote?.companyCoversFees ?? false;
  // Nothing to cover (the wallet rail with no platform fee): the gift goes uncovered, and the row
  // locks with the reason.
  const nothingToCover = hasNothingToCover(figures);
  const covered = heldRequest?.covered ?? (companyCoversFees || (coverFees && !nothingToCover));
  const breakdown = shownQuote === null ? null : breakdownFor(shownQuote, covered);
  // A wallet gift debits the gross the dialog states, which covering the fees raises above the gift
  // (CAM-R80): over the balance, it is short before any charge. A held request is resent as it was,
  // never held back: its first attempt may already have debited the wallet (invariant 1).
  const grossCents = breakdown === null ? null : parseCentsAmount(breakdown.amountCharged);
  const walletShort =
    heldRequest === null &&
    funding.kind === 'wallet' &&
    walletAvailableCents !== null &&
    grossCents !== null &&
    grossCents > walletAvailableCents;
  const rowCopy = coverFeesRowCopy({ figures, rail });
  const coverFeesLocked =
    busy ||
    phase.status === 'retryable-error' ||
    shownQuote === null ||
    companyCoversFees ||
    nothingToCover;

  // The ONLY close path. Routes the close through the right page callback: a success-state close →
  // `onCloseAfterSuccess` (toast + reset); a close after a non-success attempt → `onCloseAfterAttempt`
  // (refresh the reads the user is told to check); a plain no-attempt cancel → neither.
  const finalizeClose = useCallback(() => {
    if (phase.status === 'success') {
      onCloseAfterSuccess();
    } else if (attempted.current) {
      onCloseAfterAttempt();
    }
    onOpenChange(false);
  }, [phase.status, onCloseAfterSuccess, onCloseAfterAttempt, onOpenChange]);

  // Dismissal gate: never close mid-flight; a maybe-charged (`retryable-error`) dismissal ALWAYS
  // routes through the explicit acknowledgement step (Esc / overlay-click / Cancel arm it, only the
  // ack button actually closes); everything else closes normally.
  const requestClose = useCallback(() => {
    if (busy) return;
    if (phase.status === 'retryable-error') {
      setCloseAck(true);
      return;
    }
    finalizeClose();
  }, [busy, phase.status, finalizeClose]);

  const runCharge = useCallback(async () => {
    if (target === null) return;
    if (inFlight.current) return; // single-flight: a second click is a no-op.

    // The request this charge sends (invariant 1): the one held for this gift and tick — "Try again",
    // or the same gift re-entered after a close — exactly as it was sent; else a new one, stated from
    // what the dialog shows now.
    const signature: GiveSignature = {
      targetType: target.type,
      targetId: target.id,
      amount,
      funding,
      coverFees,
    };
    let request: GiveRequest | null = series.heldFor(signature);
    if (request === null) {
      // Never charge without the disclosure and the price shown (invariants 7, 8), nor debit the
      // wallet past its balance (CAM-R80) — the control is disarmed too.
      if (disclosure === null || liveQuote === null || walletShort) return;
      request = {
        signature,
        body: buildCompanyDonationBody({
          amount,
          funding,
          disclosure,
          coverFees: covered,
          stated: statedCharge(breakdownFor(liveQuote, covered)),
        }),
        quote: liveQuote,
        covered,
        disclosure,
      };
    }
    inFlight.current = true;
    attempted.current = true;

    // Mint (or REUSE, on a retry of this SAME gift) the stable key BEFORE the request, held with the
    // request it is for. A same-gift retry gets the SAME value AND resends the same body (the backend
    // de-dupes on both); an edited gift — the tick included — is a different signature → fresh key.
    // The echo and the stated figures are not part of the signature: a held request is resent as it
    // was, and a new one is stated afresh only once the 409 re-reads (`DISCLOSURE_STALE`,
    // `CHARGE_QUOTE_CHANGED`) or a definite no-charge have dropped the series. (Before CAM-R59b a
    // reopen after a registry move sent the held key with a NEW echo: the backend refused it as a
    // reused key — nothing charged, but the dialog then dropped the key and the next press charged
    // under a fresh one.)
    const key = series.keyFor(request);
    const { body } = request;
    const headers = { 'Idempotency-Key': key };

    try {
      setPhase({ status: 'charging' });
      // Route by target type. The company-funding body is identical across all three; only the path
      // param + SDK op differ. `throwOnError` surfaces a GivmoError we classify below.
      if (target.type === 'charity') {
        await sdk.donationsCreateCharityDonation({
          path: { charity_id: target.id },
          body,
          headers,
          throwOnError: true,
        });
      } else if (target.type === 'etf') {
        await sdk.donationsCreateEtfDonation({
          path: { etf_id: target.id },
          body,
          headers,
          throwOnError: true,
        });
      } else {
        await sdk.donationsCreateFundraiserDonation({
          path: { fundraiser_id: target.id },
          body,
          headers,
          throwOnError: true,
        });
      }

      // Confirmed: DROP the series (a brand-new gift gets a brand-new series), release the flight
      // lock, hand success to the page (invalidate wallet + dashboard), and show the forked receipt
      // copy.
      series.clear();
      inFlight.current = false;
      onSuccess();
      setPhase({ status: 'success' });
    } catch (caught: unknown) {
      inFlight.current = false;
      const err = isGivmoError(caught)
        ? caught
        : new GivmoError({ message: 'Something went wrong. Please try again.', kind: 'unknown' });

      // DISCLOSURE STALE (409): the gate refused the echo BEFORE any charge — the text changed while
      // the dialog was open. Drop the key, re-resolve, and ask for a fresh confirm against the new text.
      if (isDisclosureStaleError(err)) {
        series.clear();
        setPhase({ status: 'disclosure-stale' });
        reloadDisclosure();
        return;
      }

      // INSUFFICIENT FUNDS (409): a definite no-charge, its own first-class state. Drop the key — a
      // fresh attempt (after adding funds / switching to a card) is a genuinely new series.
      if (isInsufficientFundsError(err)) {
        series.clear();
        setPhase({ status: 'insufficient-funds', message: INSUFFICIENT_FUNDS_MESSAGE });
        return;
      }

      const reference = supportReference(err);
      // RETRYABLE (maybe-charged — incl. a 502 card decline, and a 409 IDEMPOTENCY_IN_PROGRESS or
      // IDEMPOTENCY_KEY_REUSED: the first request is still running or has succeeded): KEEP the series
      // so the next attempt re-POSTs the SAME request and the backend replays it. Explicit
      // check-status/try-again — NEVER auto-retry. Its line is the server's own sentence where the
      // server worded one, never a processor's message (EP-WEB34).
      if (isRetryableChargeError(err)) {
        setPhase({
          status: 'retryable-error',
          message: maybeChargedLine(err, RETRYABLE_HINT),
          reference,
        });
        return;
      }

      // FATAL (definite no-charge — 400/403/422/409): drop the series, so the next attempt is stated
      // afresh under a fresh key. The consumer idempotency seam RELEASES a key on a 4xx rather than
      // caching it, so this is about a stale request, not a cached refusal.
      series.clear();
      if (firstErrorCode(err) === CHARGE_QUOTE_CHANGED) {
        // The stated figures are no longer what the charge would take (a card-brand fee row, a
        // re-rate, the company's own setting), refused before any money moved. Discard the refused
        // price and read it again. RESET, not invalidate: an invalidated query keeps its data while
        // it refetches, so the dialog would state the refused total with Give live; reset empties
        // it, so until the new price lands no figure is stated and Give is held.
        void queryClient.resetQueries({ queryKey: giftQuoteQueryKey(wireAmount, funding) });
        setPhase({ status: 'quote-changed' });
        return;
      }
      setPhase({ status: 'fatal-error', message: err.message, reference });
    }
  }, [
    target,
    coverFees,
    disclosure,
    liveQuote,
    walletShort,
    covered,
    amount,
    funding,
    wireAmount,
    series,
    queryClient,
    onSuccess,
    reloadDisclosure,
  ]);

  const isSuccess = phase.status === 'success';
  const title = isSuccess ? 'Donation sent' : 'Confirm your donation';
  const targetName = target?.name ?? 'this cause';

  // Fork the confirm verb by funding source (charges a card vs. debits the wallet), stating the
  // server's gross (invariant 8). The fee is the row's line directly above, so it is not restated,
  // and ticking the row changes one figure without reflowing the dialog. While there is no price the
  // line says why Give is held.
  const chargedFigure =
    breakdown === null
      ? null
      : breakdown.coverFees
        ? formatFeeFigure(breakdown.amountCharged)
        : formatChargeFigure(breakdown.amountCharged);
  const confirmVerb =
    chargedFigure !== null
      ? funding.kind === 'wallet'
        ? `This debits ${chargedFigure} from your company wallet immediately.`
        : `This charges ${chargedFigure} to your company card immediately.`
      : quoteStatus === 'failed'
        ? QUOTE_FAILED
        : funding.kind === 'wallet'
          ? QUOTE_LOADING_WALLET
          : QUOTE_LOADING_CARD;

  const receiptLine = receiptCopy(funding);

  // A short wallet is said before any charge in the words and state of the server's refusal
  // (CAM-R80), in place of an earlier no-charge notice too (a re-read price or balance can make the
  // gift short after one), and goes again once the gross fits (unticking the fees).
  const shownPhase: Phase =
    walletShort &&
    (phase.status === 'idle' ||
      phase.status === 'fatal-error' ||
      phase.status === 'quote-changed' ||
      phase.status === 'disclosure-stale')
      ? { status: 'insufficient-funds', message: INSUFFICIENT_FUNDS_MESSAGE }
      : phase;

  const errorState =
    shownPhase.status === 'retryable-error' ||
    shownPhase.status === 'insufficient-funds' ||
    shownPhase.status === 'fatal-error' ||
    shownPhase.status === 'disclosure-stale' ||
    shownPhase.status === 'quote-changed';

  // Give is armed once the disclosure and the price are in (invariants 7, 8); a held request — "Try
  // again", or the same gift re-entered — is resent as it was and needs neither afresh (invariant 1).
  // A short wallet never arms it (CAM-R80).
  const canGive =
    !busy &&
    !walletShort &&
    shownPhase.status !== 'insufficient-funds' &&
    (heldRequest !== null || (disclosure !== null && quoteStatus === 'ready'));

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Every dismiss vector (Esc, overlay-click, Cancel/Close) routes through the gate: an
        // in-flight charge never closes, and a maybe-charged state closes only via acknowledgement.
        if (!next) {
          requestClose();
          return;
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={title}
        className="flex flex-col gap-16"
        width={480}
        testId={`${PREFIX}-confirm-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>

        {isSuccess ? (
          // ── Success: forked receipt copy (CARD ⇒ receipt emailed; WALLET ⇒ no receipt mention). ──
          <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-success`)}>
            <div className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-12">
              <Icon icon={CheckCircle2} size={18} aria-hidden className="mt-1 text-success-600" />
              <div className="flex flex-col gap-4">
                <p className="font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong">
                  You gave {formatWholeDollarUsd(amount)} to {targetName}.
                </p>
                {receiptLine !== null && (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    {...testIdProp(`${PREFIX}-success-receipt`)}
                  >
                    {receiptLine}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-8">
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                onClick={finalizeClose}
                testId={`${PREFIX}-done`}
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* ── The donation itself is the focal point (D-CAM-41): what, to whom, from where, and
                who the donor of record is. ── */}
            <ChargeSummary
              mark={target !== null ? { name: target.name, logoUrl: target.logoUrl } : null}
              amount={formatWholeDollarUsd(amount)}
              recipient={targetName}
              // A Cause ETF or a fundraiser says what it is; a charity's category adds nothing here.
              context={giveSummaryContext(target)}
              rows={[
                {
                  label: 'From',
                  value: fundingSummary ?? fundingLabel,
                  testId: `${PREFIX}-summary-from`,
                },
                {
                  label: 'Donor of record',
                  value: companyName ?? 'Your company',
                  testId: `${PREFIX}-summary-donor`,
                },
              ]}
              testId={`${PREFIX}-summary`}
            />

            {/* ── "Cover processing fees" (invariant 8): under the amount, off on every open, its
                line the server's fee. Locked while there is no price, while a charge is in flight or
                in doubt, when the company covers every charge, and when there is nothing to cover. ── */}
            <CoverFeesRow
              checked={
                heldRequest !== null
                  ? heldRequest.covered
                  : quoteStatus === 'failed'
                    ? false
                    : covered
              }
              onCheckedChange={(next) => {
                setCoverFees(next);
                // A new, un-charged attempt: a prior no-charge notice goes. The choice is part of
                // the series signature, so the next charge keys itself.
                setPhase((current) =>
                  current.status === 'fatal-error' ||
                  current.status === 'quote-changed' ||
                  current.status === 'disclosure-stale'
                    ? { status: 'idle' }
                    : current,
                );
              }}
              disabled={coverFeesLocked}
              title={rowCopy.title}
              line={rowCopy.line}
              lineIsReason={rowCopy.lineIsReason}
              testId={`${PREFIX}-cover-fees`}
            />

            <Dialog.Description
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${PREFIX}-charge-line`)}
            >
              {confirmVerb}
            </Dialog.Description>

            {errorState && (
              <div
                className="flex flex-col items-start gap-2"
                {...testIdProp(
                  shownPhase.status === 'retryable-error'
                    ? `${PREFIX}-retryable-error`
                    : shownPhase.status === 'insufficient-funds'
                      ? `${PREFIX}-insufficient-funds`
                      : shownPhase.status === 'disclosure-stale'
                        ? `${PREFIX}-disclosure-stale`
                        : shownPhase.status === 'quote-changed'
                          ? `${PREFIX}-quote-changed`
                          : `${PREFIX}-fatal-error`,
                )}
              >
                <div className="flex items-start gap-8">
                  <Icon
                    icon={shownPhase.status === 'insufficient-funds' ? WalletIcon : AlertTriangle}
                    size={16}
                    aria-hidden
                    className="mt-1 text-danger-700"
                  />
                  <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                    {shownPhase.status === 'disclosure-stale'
                      ? DISCLOSURE_STALE_MESSAGE
                      : shownPhase.status === 'quote-changed'
                        ? QUOTE_CHANGED_MESSAGE
                        : shownPhase.message}
                  </p>
                </div>
                {(shownPhase.status === 'retryable-error' || shownPhase.status === 'fatal-error') &&
                  shownPhase.reference !== null && (
                    <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                      {shownPhase.reference}
                    </p>
                  )}
              </div>
            )}

            {shownDisclosure === null && disclosureState.status === 'unavailable' && (
              // ── The disclosure did not resolve (invariant 7): Give stays disarmed; say why. ──
              <DisclosureUnavailable
                message={DISCLOSURE_UNAVAILABLE_MESSAGE}
                onRetry={reloadDisclosure}
                testId={`${PREFIX}-disclosure-unavailable`}
              />
            )}

            {/* ── The donor disclosure (invariant 7), directly above the confirm control: the body a
                held request was stated under, or the one just resolved. ── */}
            {shownDisclosure !== null && (
              <ChargeDisclosureTerms
                sections={[
                  {
                    key: shownDisclosure.contentHash,
                    label: null,
                    lines: shownDisclosure.lines,
                  },
                ]}
                testId={`${PREFIX}-disclosure`}
              />
            )}

            {phase.status === 'retryable-error' && closeAck ? (
              // ── Maybe-charged close acknowledgement (invariant 2): the footer is replaced so the
              //    ONLY ways forward are an explicit acknowledged close or going back to Try again. ──
              <div
                ref={closeAckRef}
                className="flex scroll-mb-24 flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12"
                {...testIdProp(`${PREFIX}-close-ack`)}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary">
                  {CLOSE_ACK_BODY}
                </p>
                <div className="flex items-center justify-end gap-8">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    onClick={() => setCloseAck(false)}
                    testId={`${PREFIX}-close-ack-back`}
                  >
                    Go back
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="md"
                    onClick={finalizeClose}
                    testId={`${PREFIX}-close-ack-confirm`}
                  >
                    I understand — close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-end gap-8">
                <Dialog.Close
                  disabled={busy}
                  render={<Button appearance="saas" tone="secondary" size="md" />}
                >
                  Cancel
                </Dialog.Close>
                <Button
                  appearance="saas"
                  tone="primary"
                  size="md"
                  // Single-flight: disabled + spinning while in flight. A double-click cannot fire a
                  // second POST (this + the `inFlight` ref) and cannot mint a second key. Insufficient
                  // funds is fatal (no retry) — the user must change funding, so no Confirm is offered.
                  // Disarmed until the disclosure and the price are in (spinning while they load).
                  disabled={!canGive}
                  loading={
                    busy ||
                    (heldRequest === null &&
                      (disclosureState.status === 'loading' || quoteStatus === 'loading'))
                  }
                  onClick={() => void runCharge()}
                  testId={`${PREFIX}-confirm-submit`}
                >
                  {phase.status === 'retryable-error'
                    ? 'Try again'
                    : `Donate ${formatWholeDollarUsd(amount)}`}
                </Button>
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
